import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import "../css/Header.css";
import {
    Search,
    Bell,
    Menu,
    ChevronDown,
    Settings,
    LogOut
} from 'lucide-react';

function Header({ toggleSidebar }) {
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const navigate = useNavigate();
    const profileMenuRef = useRef(null);

    // Default profile icon SVG
    const defaultProfileIcon = `data:image/svg+xml;base64,${btoa(`
        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
        </svg>
    `)}`;

    const [profileImage, setProfileImage] = useState(localStorage.getItem('profileImage') || defaultProfileIcon);

    // Update profile image when changed in localStorage
    useEffect(() => {
        const handleStorageChange = () => {
            const updatedImage = localStorage.getItem('profileImage') || defaultProfileIcon;
            setProfileImage(updatedImage);
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [defaultProfileIcon]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
                setShowProfileMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleProfileMenu = () => {
        setShowProfileMenu(!showProfileMenu);
    };

    const handleLogoutClick = () => {
        setShowLogoutModal(true);
        setShowProfileMenu(false);
    };

    const confirmLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('profileImage');
        setShowLogoutModal(false);
        navigate('/');
    };

    const cancelLogout = () => {
        setShowLogoutModal(false);
    };

    return (
        <header className="header-container">
            <div className="header-left">
                <button className="mobile-menu-toggle" onClick={toggleSidebar}>
                    <Menu size={24} />
                </button>
                <div className="logo">
                    <Link to="/dashboard" className="logo-link">
                        <div className="logo-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#FFD700" stroke="#FFD700" strokeWidth="2" />
                                <path d="M5 9V16L12 20L19 16V9" stroke="#FFD700" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </div>
                        <span className="logo-text">Manage School</span>
                    </Link>
                </div>
            </div>

            <div className="header-center">
                <div className="search-container">
                    <Search size={18} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search here..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        className="search-input"
                    />
                </div>
            </div>

            <div className="header-right">
                <div className="notification-icon">
                    <Bell size={20} />
                    <span className="notification-badge">3</span>
                </div>

                <div className="profile-container1" ref={profileMenuRef}>
                    <div className="profile-info" onClick={toggleProfileMenu}>
                        <div className="profile-avatar">
                            <img src={profileImage} alt="Profile" onError={(e) => { e.target.src = defaultProfileIcon }} />
                        </div>
                        <div className="profile-name">
                            {localStorage.getItem('role') || 'User'}
                            <ChevronDown size={14} />
                        </div>
                    </div>

                    {showProfileMenu && (
                        <div className="profile-dropdown">
                            <ul>
                                <li><Link className="logout-link"><Settings size={16} /> Settings</Link></li>
                                <li onClick={handleLogoutClick} className="logout-link"><LogOut size={16} /> Logout</li>
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            {showLogoutModal && (
                <div className="logout-modal-overlay">
                    <div className="logout-modal">
                        <h3 className="logout-modal-title">Confirm Logout</h3>
                        <p className="logout-modal-message">Are you sure you want to logout?</p>
                        <div className="logout-modal-actions">
                            <button className="logout-modal-cancel" onClick={cancelLogout}>
                                Cancel
                            </button>
                            <button className="logout-modal-confirm" onClick={confirmLogout}>
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}

export default Header;